/**
 * The left column of the chat: three cards, in the order the platform works in.
 * What it is deciding, what it knows, and what it told the agent this turn.
 *
 * Every card folds away, so the reader can give the whole column to whichever
 * one they are working from.
 */
import { type ReactNode, useCallback, useMemo, useState } from "react";
import {
  Outcome,
  ProductTree,
} from "@financial-advisory/data-access/api-types";
import { getTreeTypeFromId } from "@unlikelyai-magic/utils";
import type {
  ChangedNode,
  QuestionAnswer,
  SystemState,
} from "@unlikelyai-magic/utils";
import { EmptySystemThinkingPlaceholder } from "./EmptySystemThinkingPlaceholder";
import { GraphModal } from "./GraphModal";
import { LiveTurnStatus } from "./LiveTurnStatus";
import { buildBriefing } from "./briefing";
import {
  BriefingCard,
  BriefingEmptyState,
  OutcomeCapabilitiesCard,
  UserKnowledgeSection,
} from "./components";
import type { ConversationPair } from "./types";

interface SystemThinkingPanelProps {
  productTreesById: Map<string, ProductTree>;
  questionAnswers: QuestionAnswer[];
  conversations: ConversationPair[];
  selectedIndex: number | null;
  systemState: SystemState;
  allNodes: Map<string, ChangedNode>;
  /**
   * Called when an answered question is clicked, to scroll the chat to the turn
   * that produced it.
   */
  onSelectKnowledge?: (question: string) => void;
  /**
   * Called when the reader steps to another turn.
   */
  onSelectTurn?: (index: number) => void;
  /**
   * True while the panel moves itself on as each turn lands.
   */
  isFollowingLive?: boolean;
  /**
   * True while a turn is still being worked out, so the panel is a turn behind
   * the chat on purpose.
   */
  isEvaluatingTurn?: boolean;
  /**
   * True while a simulation is running or a reply is on its way.
   */
  isLive?: boolean;
  onFollowLive?: () => void;
}

const GRAPH_MODAL_DESCRIPTION =
  "During the conversation, messages are passed to the UnlikelyAI platform for real-time evaluation. " +
  "This is where our core neurosymbolic technology plugs in — each node in this tree is evaluated " +
  "automatically as new information emerges from the conversation.";

function getGraphExplanation(
  tree: ProductTree,
  systemState: SystemState
): ReactNode {
  const treeType = getTreeTypeFromId(tree.id);
  const stateMap =
    treeType === "situation"
      ? systemState.situations
      : treeType === "segment"
        ? systemState.segments
        : systemState.suggestions;

  const outcome = stateMap.get(tree.id)?.value;
  const name = <strong>{tree.name}</strong>;

  const summaryLine =
    outcome === Outcome.TRUE ? (
      <>The assistant may raise {name} with this customer.</>
    ) : outcome === Outcome.FALSE ? (
      <>The assistant must not raise {name} with this customer.</>
    ) : (
      <>It is not yet known whether {name} may be raised.</>
    );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <div>{summaryLine}</div>
      <div style={{ fontSize: "14px", color: "#64748b" }}>
        {GRAPH_MODAL_DESCRIPTION}
      </div>
    </div>
  );
}

interface ExpandedCards {
  outcomes: boolean;
  knowledge: boolean;
  turn: boolean;
}

export const SystemThinkingPanel = ({
  productTreesById,
  questionAnswers,
  conversations,
  selectedIndex,
  systemState,
  allNodes,
  onSelectKnowledge,
  onSelectTurn,
  isFollowingLive = true,
  isEvaluatingTurn = false,
  isLive = false,
  onFollowLive,
}: SystemThinkingPanelProps) => {
  const [graphToShow, setGraphToShow] = useState<string | null>(null);
  // The turn is the card the reader works from, so it opens first. Knowledge is
  // long, so it starts folded and the header count says how much is in it.
  const [expanded, setExpanded] = useState<ExpandedCards>({
    outcomes: true,
    knowledge: false,
    turn: true,
  });

  const toggleCard = useCallback((card: keyof ExpandedCards) => {
    setExpanded((previous) => ({ ...previous, [card]: !previous[card] }));
  }, []);

  const handleCloseModal = useCallback(() => setGraphToShow(null), []);

  const selectedConversation =
    selectedIndex !== null
      ? conversations.find(
          (conversation) => conversation.index === selectedIndex
        )
      : undefined;

  const briefing = useMemo(
    () =>
      selectedConversation
        ? buildBriefing(selectedConversation, conversations, allNodes)
        : null,
    [selectedConversation, conversations, allNodes]
  );

  /**
   * Stepping moves between customer messages, not between pairs, so a step
   * always lands on a turn that has been worked out.
   */
  const turnIndexes = useMemo(
    () =>
      conversations
        .filter((conversation) => conversation.userMessage?.trim())
        .map((conversation) => conversation.index),
    [conversations]
  );

  const turnPosition = useMemo(() => {
    if (!selectedConversation) return -1;
    const exact = turnIndexes.indexOf(selectedConversation.index);
    if (exact !== -1) return exact;
    // A pair holding only the reply belongs to the turn the last message started.
    return (
      turnIndexes.filter((index) => index < selectedConversation.index).length -
      1
    );
  }, [turnIndexes, selectedConversation]);

  const stepToTurn = useCallback(
    (position: number) => onSelectTurn?.(turnIndexes[position]),
    [onSelectTurn, turnIndexes]
  );

  // The tags on open questions follow whichever turn is in view, so the knowledge
  // card and the briefing always tell the same story.
  const latestConversation = conversations[conversations.length - 1];
  const tagSource = selectedConversation ?? latestConversation;

  if (conversations.length === 0) {
    return <EmptySystemThinkingPlaceholder />;
  }

  const tree = graphToShow ? productTreesById.get(graphToShow) : undefined;
  const canStepBack = onSelectTurn !== undefined && turnPosition > 0;
  const canStepOn =
    onSelectTurn !== undefined &&
    turnPosition >= 0 &&
    turnPosition < turnIndexes.length - 1;

  return (
    <>
      {tree && (
        <GraphModal
          key={graphToShow}
          productTree={tree}
          questionAnswers={questionAnswers}
          onClose={handleCloseModal}
          explanation={getGraphExplanation(tree, systemState)}
        />
      )}
      <div className="flex h-full flex-col gap-4 overflow-hidden">
        <LiveTurnStatus
          isLive={isLive}
          isFollowingLive={isFollowingLive}
          isEvaluatingTurn={isEvaluatingTurn}
          turnNumber={
            // On the opening turn the panel is already on the turn being worked
            // out, so there is no earlier turn to say it is holding on.
            briefing?.isEvaluating ? undefined : briefing?.turnNumber
          }
          onFollowLive={onFollowLive}
        />

        <OutcomeCapabilitiesCard
          systemState={systemState}
          isExpanded={expanded.outcomes}
          onToggle={() => toggleCard("outcomes")}
          onSelectOutcome={setGraphToShow}
        />

        <UserKnowledgeSection
          questionAnswers={questionAnswers}
          allNodes={allNodes}
          isExpanded={expanded.knowledge}
          onToggle={() => toggleCard("knowledge")}
          askingNext={tagSource?.targetQuestions?.[0]}
          contradictingQuestions={tagSource?.contradictingQuestions}
          onSelectKnowledge={onSelectKnowledge}
        />

        {briefing ? (
          <BriefingCard
            briefing={briefing}
            isExpanded={expanded.turn}
            onToggle={() => toggleCard("turn")}
            turnCount={Math.max(turnIndexes.length, 1)}
            onPreviousTurn={
              canStepBack ? () => stepToTurn(turnPosition - 1) : undefined
            }
            onNextTurn={
              canStepOn ? () => stepToTurn(turnPosition + 1) : undefined
            }
          />
        ) : (
          <BriefingEmptyState />
        )}
      </div>
    </>
  );
};
